import { localDevLog, localDevWarn } from '../debug/localDevLogger';

export const getPlaceholderImageUrl = (word) =>
  `https://placehold.co/640x480/333333/FFFFFF?text=${word}`;

export const fetchPixabayImage = async (imageId) => {
  try {
    localDevLog(`🖼️ Pixabay: Fetching image with ID ${imageId}...`);

    // Call Vercel serverless proxy so we never ship API keys to the browser.
    const response = await fetch(`/api/pixabayImage?id=${encodeURIComponent(String(imageId))}`);
    if (!response.ok) {
      throw new Error(`Pixabay proxy error (status ${response.status})`);
    }

    const data = await response.json();
    if (data?.imageUrl) {
      localDevLog(`✅ Pixabay proxy success: Found image`);
      return data.imageUrl;
    }

    // Fallback: direct download URL sometimes works even without an API call.
    localDevLog(`🔄 Pixabay proxy returned no imageUrl, trying direct URL`);
    return `https://pixabay.com/images/download/${imageId}.jpg`;
  } catch (error) {
    localDevWarn(`❌ Pixabay API error for ID ${imageId}:`, error.message);
    return `https://pixabay.com/images/download/${imageId}.jpg`;
  }
};

export const fetchPexelsImage = async (imageId) => {
  try {
    localDevLog(`🖼️ Pexels: Fetching image with ID ${imageId}...`);
    const directUrl = `https://images.pexels.com/photos/${imageId}/pexels-photo-${imageId}.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260`;

    // Call Vercel serverless proxy so we never ship API keys to the browser.
    try {
      localDevLog(`📡 Calling Pexels proxy for ID ${imageId}...`);
      const response = await fetch(`/api/pexelsImage?id=${encodeURIComponent(String(imageId))}`);
      if (response.ok) {
        const data = await response.json();
        if (data?.imageUrl) {
          localDevLog(`✅ Pexels proxy success: Found image`);
          return data.imageUrl;
        }
      }
    } catch (apiError) {
      localDevWarn(`⚠️ Pexels proxy error, using direct URL instead:`, apiError?.message);
    }

    localDevLog(`🔄 Using direct Pexels URL`);
    return directUrl;
  } catch (error) {
    console.error(`❌ Pexels fetch error:`, error.message);
    console.error('Error fetching Pexels image:', error);
    throw error;
  }
};

export const isImageAccessible = (url) => {
  localDevLog(`🔍 Testing if image is accessible: ${url.substring(0, 50)}...`);
  return new Promise((resolve) => {
    const img = new Image();
    const timeout = setTimeout(() => {
      localDevLog(`⏱️ Timeout: Image took too long to load`);
      resolve(false);
    }, 8000);

    img.onload = () => {
      clearTimeout(timeout);
      localDevLog(`👍 Image loaded successfully`);
      resolve(true);
    };

    img.onerror = () => {
      clearTimeout(timeout);
      localDevLog(`👎 Image failed to load`);
      resolve(false);
    };

    img.src = url;
  });
};

export const preloadImage = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => reject(false);
    img.src = url;
  });
